import { Rnd } from 'react-rnd';
import { clamp } from 'lodash';
import { IconX } from '@tabler/icons-react';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Label, RemoveButton, Shell } from './FieldBindingBox.styled.ts';
import type { BrowserBox, FieldBindingBoxBounds, HighlightState } from './FieldBindingBox.types.ts';

interface Props {
	label: string;
	state: HighlightState;
	browserBox: BrowserBox;
	isSelected: boolean;
	bounds: FieldBindingBoxBounds;
	onChange: (next: BrowserBox) => void;
	onSelect: () => void;
	onRemove: () => void;
}

export const FieldBindingBox = ({
	label,
	state,
	browserBox,
	isSelected,
	bounds,
	onChange,
	onSelect,
	onRemove,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const handleDragStop = (_e: unknown, data: { x: number; y: number }) => {
		const maxLeft = Math.max(0, bounds.width - browserBox.width);
		const maxTop = Math.max(0, bounds.height - browserBox.height);
		onChange({
			left: clamp(data.x, 0, maxLeft),
			top: clamp(data.y, 0, maxTop),
			width: browserBox.width,
			height: browserBox.height,
		});
	};

	const handleResizeStop = (
		_e: unknown,
		_dir: unknown,
		ref: HTMLElement,
		_delta: unknown,
		position: { x: number; y: number }
	) => {
		const width = parseFloat(ref.style.width);
		const height = parseFloat(ref.style.height);
		const maxLeft = Math.max(0, bounds.width - width);
		const maxTop = Math.max(0, bounds.height - height);
		onChange({
			left: clamp(position.x, 0, maxLeft),
			top: clamp(position.y, 0, maxTop),
			width,
			height,
		});
	};

	return (
		<Rnd
			size={{ width: browserBox.width, height: browserBox.height }}
			position={{ x: browserBox.left, y: browserBox.top }}
			minWidth={12}
			minHeight={10}
			// The remove (×) button overlays the top-right corner when selected;
			// disable that corner's resize handle then so clicks hit the button
			// instead of starting a resize.
			enableResizing={{
				top: true,
				right: true,
				bottom: true,
				left: true,
				topLeft: true,
				bottomRight: true,
				bottomLeft: true,
				topRight: !isSelected,
			}}
			onDragStart={onSelect}
			onResizeStart={onSelect}
			onDragStop={handleDragStop}
			onResizeStop={handleResizeStop}
			style={{ zIndex: isSelected ? 3 : 2 }}
		>
			<Shell
				$state={state}
				$selected={isSelected}
				onMouseDown={onSelect}
				onClick={onSelect}
				data-testid="field-binding-box"
			>
				<Label title={label}>{label}</Label>
				{isSelected && (
					<RemoveButton
						type="button"
						aria-label={tMipManage(
							'master_documents.field_binding.canvas.remove_field_aria',
							{ label }
						)}
						onMouseDown={(event) => event.stopPropagation()}
						onClick={(event) => {
							event.stopPropagation();
							onRemove();
						}}
					>
						<IconX size={12} />
					</RemoveButton>
				)}
			</Shell>
		</Rnd>
	);
};
